<template>
  <div class="flex flex-col bg-surface-1 border border-border-separator rounded-md">
    <!-- Collapsed header. Always present, so creating a task is visible from the task list without a
         mode change — the design doc's manager puts the form and the list on one scroll. It stays
         COLLAPSED by default rather than expanded as the mockup draws it: in this panel's width the
         open form is most of a screen, and someone arriving to check whether a recap ran should not
         have to scroll past a form they did not come for. -->
    <div
      class="flex items-center gap-2 px-3 py-2.5 min-h-[52px]"
      :class="disabled ? 'cursor-not-allowed' : 'cursor-pointer'"
      role="button"
      :aria-expanded="expanded ? 'true' : 'false'"
      :tabindex="disabled ? -1 : 0"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <i
        class="ri-add-line ri-125 flex-shrink-0"
        :class="disabled ? 'text-text-subtle' : 'text-text-link'"
      />
      <span class="text-sm font-medium flex-1" :class="disabled ? 'text-text-subtle' : 'text-text-default'">
        New scheduled task
      </span>
      <i
        v-if="!disabled"
        class="ri-arrow-down-s-line ri-125 text-text-subtle transition-transform"
        :class="expanded ? 'rotate-180' : ''"
      />
    </div>

    <!-- The refusal explains itself where the control is, rather than as a banner elsewhere that the
         person has to connect to the thing that is not working. -->
    <div v-if="disabled" class="px-3 pb-2.5 text-xs text-text-subtle">
      {{ disabledReason }}
    </div>

    <div v-else-if="expanded" class="flex flex-col gap-4 px-3 pb-3 border-t border-border-separator pt-3 bg-surface-3/60 rounded-b-md">
      <scheduled-type-cards
        v-model="form.type"
        :watcher-available="true"
        :events-available="event_types.length > 0"
        :watchers-enabled="watchers_enabled"
        @update:modelValue="onTypeChanged"
      />

      <!-- Name. Recap only: a watcher is named from the line it watches, which is the sentence the
           person just typed — asking them to name it as well is asking the same question twice. -->
      <label v-if="is_recap" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-text-default">Name</span>
        <input
          v-model="form.name"
          type="text"
          maxlength="120"
          name="taskName"
          placeholder="Morning sales recap"
          class="text-sm px-2 py-1.5 bg-surface-1 border border-border-separator rounded-md text-text-default outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
        />
      </label>

      <!-- Recap: the question to answer on a timer. -->
      <label v-if="is_recap" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-text-default">Question to answer on a timer</span>
        <textarea
          v-model="form.prompt"
          rows="3"
          name="taskPrompt"
          placeholder="Send me yesterday's sales recap by make."
          class="text-sm px-2 py-1.5 bg-surface-1 border border-border-separator rounded-md text-text-default resize-y outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
        />
      </label>

      <!-- Watcher: the line to watch, in the person's own words. -->
      <div v-if="is_watcher" class="flex flex-col gap-1">
        <label class="flex flex-col gap-1">
          <span class="flex items-center gap-1">
            <span class="text-xs font-medium text-text-default flex-1">What should Rey watch for?</span>
            <!-- How to write a sentence the catalogue can read, one hover away. Inside the label, so a click
                 is prevented: it would otherwise put the caret in the box. -->
            <span
              v-tooltip="$t('scheduled.create.watchHelp')"
              data-test="watch-help"
              tabindex="0"
              role="img"
              :aria-label="$t('scheduled.create.watchHelp')"
              class="flex text-text-subtle cursor-help"
              @click.prevent
            ><i class="ri-information-line" /></span>
          </span>
          <textarea
            v-model="form.watch_text"
            rows="3"
            name="watchText"
            placeholder="Tell me when any RO passes 5 days"
            class="text-sm px-2 py-1.5 bg-surface-1 border border-border-separator rounded-md text-text-default resize-y outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
            @input="clearCompiled"
          />
        </label>
        <!-- Stated up front rather than discovered through a refusal. A watcher resolves against a
             reviewed catalogue, so most sentences cannot be watched — and a blank box that accepts
             anything and then rejects most of it teaches nothing. -->
        <span class="text-xs text-text-subtle">
          Rey can watch aging repair orders, inventory age, large deals, and contracts in transit.
        </span>
      </div>

      <!-- Watcher cadence. One option, and it is still a control: it states what "continuously" actually
           means, which is hourly — the frequency floor. A label alone would leave the person to assume
           something tighter. -->
      <label v-if="is_watcher" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-text-default">How often to check</span>
        <fusion-dropdown
          v-model="form.cadence"
          name="watchCadence"
          :options="watch_cadence_options"
          sort-order="none"
          compact
        />
      </label>

      <!-- Event trigger: the happening to react to, in the person's own words. -->
      <div v-if="is_event" class="flex flex-col gap-1">
        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-text-default">What should Rey react to?</span>
          <textarea
            v-model="form.event_text"
            rows="3"
            name="eventText"
            placeholder="When a deal over $100,000 closes"
            class="text-sm px-2 py-1.5 bg-surface-1 border border-border-separator rounded-md text-text-default resize-y outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
            @input="clearCompiled"
          />
        </label>
        <span v-if="event_type_labels.length" class="text-xs text-text-subtle">
          Rey can react to: {{ event_type_labels.join(', ') }}.
        </span>
      </div>

      <!-- The optional instruction from the doc's Figure 13. It becomes the task's prompt, so Rey runs
           it as an ordinary turn when the event fires; left empty it simply reports that the thing
           happened. -->
      <label v-if="is_event" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-text-default">
          What Rey should do <span class="text-text-subtle">(optional)</span>
        </span>
        <input
          v-model="form.event_action"
          type="text"
          maxlength="500"
          name="eventAction"
          placeholder="Post the deal summary and the gross"
          class="text-sm px-2 py-1.5 bg-surface-1 border border-border-separator rounded-md text-text-default outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
        />
        <span class="text-xs text-text-subtle">No clock — Rey reacts as it happens.</span>
      </label>

      <!-- Cadence + time -->
      <div v-if="is_recap" class="flex gap-3">
        <label class="flex flex-col gap-1 flex-1">
          <span class="text-xs font-medium text-text-default">How often</span>
          <fusion-dropdown
            v-model="form.cadence"
            name="cadence"
            :options="cadences"
            sort-order="none"
            compact
          />
        </label>
        <label class="flex flex-col gap-1 w-32">
          <span class="text-xs font-medium text-text-default">Time</span>
          <!-- A dropdown rather than a native time input, whose clock popup detached from the iframe when
               the host page scrolled. -->
          <fusion-dropdown
            v-model="form.time"
            name="time"
            :options="time_dropdown_options"
            sort-order="none"
            compact
          />
        </label>
      </div>

      <!-- Day picker, per the doc: "Where the cadence is a day of the week, a day picker appears
           alongside it." -->
      <label v-if="is_recap && form.cadence === 'Weekly'" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-text-default">Day of week</span>
        <fusion-dropdown
          v-model="form.dayOfWeek"
          name="dayOfWeek"
          :options="weekday_options"
          sort-order="none"
          compact
        />
      </label>

      <label v-if="is_recap && form.cadence === 'Monthly'" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-text-default">Day of month</span>
        <input
          v-model.number="form.dayOfMonth"
          type="number"
          min="1"
          max="31"
          name="dayOfMonth"
          class="text-sm px-2 py-1.5 bg-surface-1 border border-border-separator rounded-md text-text-default w-24 outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
        />
      </label>

      <!-- Store. Every task carries one; until now the editor never asked and silently took whichever
           rooftop was active, which is the same field the per-rooftop beta gate and the run's own tool
           scoping key on. -->
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-text-default">Store</span>
        <fusion-dropdown
          v-model="form.dealerId"
          name="dealerId"
          :options="store_options"
          sort-order="none"
          compact
        />
      </label>

      <!-- The zone the task will actually fire on, stated rather than assumed. Two defects have come
           from implicit zone resolution, and the design doc's own Considerations section asks for the
           inherited zone to be shown back to the user. Editable, but behind a disclosure: the default is
           right almost always, and a picker at rest invites a change nobody needed to make. -->
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-text-default">Time zone</span>
          <span class="text-xs text-text-subtle flex-1 truncate">{{ selected_timezone_label }}</span>
          <button
            type="button"
            class="text-xs text-text-link flex-shrink-0"
            @click="editing_timezone = !editing_timezone"
          >{{ editing_timezone ? 'Done' : 'Change' }}</button>
        </div>
        <fusion-dropdown
          v-if="editing_timezone"
          v-model="form.timeZone"
          name="timeZone"
          :options="timezone_dropdown_options"
          sort-order="none"
          compact
        />
      </div>

      <!-- Delivery. The design doc says no task takes a delivery target; the shipped engine disagrees and
           email has a working transport, so the choice stays offered. In-app is the floor — every channel
           off is a task that runs and tells nobody. -->
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-text-default">Where it goes</span>
        <label class="flex items-center gap-2 text-sm text-text-default">
          <input
            type="checkbox"
            name="inAppChannel"
            :checked="hasChannel('InApp')"
            @change="setChannel('InApp', $event.target.checked)"
          />
          <span>In your Rey chat</span>
        </label>
        <label class="flex items-center gap-2 text-sm text-text-default">
          <input
            type="checkbox"
            name="emailChannel"
            :checked="hasChannel('Email')"
            @change="setChannel('Email', $event.target.checked)"
          />
          <span>Email</span>
        </label>
        <label v-if="push_available" class="flex items-center gap-2 text-sm text-text-default">
          <input
            type="checkbox"
            name="pushChannel"
            :checked="hasChannel('Push')"
            @change="setChannel('Push', $event.target.checked)"
          />
          <span>Push notification on my phone</span>
        </label>
      </div>

      <!-- Restate-and-confirm. A watcher's condition is compiled ONCE at creation from words that can be
           read more than one way, so the person confirms the reading before it is saved — the doc's
           restate-and-confirm, and the reason compiling is a separate step from creating. -->
      <div
        v-if="compiled_restatement"
        class="flex flex-col gap-2 p-3 rounded-md bg-surface-2 border border-brand-outline"
      >
        <span class="text-xs font-medium text-text-default">
          {{ is_event ? 'Rey will react to this:' : 'Rey will watch for this:' }}
        </span>
        <span class="text-sm text-text-default">{{ compiled_restatement }}</span>
        <span class="text-xs text-text-subtle">
          {{ is_event ? 'It posts to your chat each time that happens.' : 'It stays quiet until that line is crossed.' }}
        </span>
      </div>

      <!-- What Rey CAN watch, when a subject resolved to nothing. Beside the box the person is still
           typing in, not as a thrown-away error: the list is the actionable half of the refusal. -->
      <div v-if="compile_error" class="flex flex-col gap-1">
        <span class="text-xs text-danger">{{ compile_error }}</span>
        <span v-if="can_watch.length" class="text-xs text-text-subtle">
          {{ is_event ? 'Rey can react to' : 'Rey can watch' }}: {{ can_watch.join(', ') }}.
        </span>
      </div>

      <div v-if="error_message" class="text-xs text-danger">{{ error_message }}</div>

      <!-- The consequence, stated before the click rather than discovered after it. The doc requires it
           per type; with only Recap creatable today there is one sentence, and consequence_line is where
           the other two land. -->
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="text-xs px-3 py-1.5 rounded bg-btn-primary text-white flex-shrink-0"
          :disabled="saving || compiling"
          :class="(saving || compiling) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
          @click="submit"
        >{{ primary_label }}</button>
        <button
          v-if="compiled_restatement"
          type="button"
          class="text-xs px-2 py-1.5 rounded bg-transparent outline outline-1 outline-brand-outline text-text-link flex-shrink-0"
          @click="clearCompiled"
        >Change it</button>
        <span class="text-xs text-text-subtle">{{ consequence_line }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import ScheduledTypeCards from '@components/Scheduled/ScheduledTypeCards.vue';
import FusionDropdown from '@components/UI/FusionDropdown.vue';
import { timezoneOptionsFor, timezoneDropdownOptions, browserTimeZone } from '../../utils/timezones';
import { weekdayDropdownOptions } from '../../utils/weekdays';
import { timeOfDayOptions } from '../../utils/timeOfDay';

export default {
  name: 'ScheduledCreate',
  components: {
    'scheduled-type-cards': ScheduledTypeCards,
    'fusion-dropdown': FusionDropdown,
  },
  props: {
    /** Creating is withdrawn — rooftop outside the beta, or a read-only caller. */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** Why, in the person's own terms. Rendered where the control would be. */
    disabledReason: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['created'],
  created() {
    this.getScheduledEventTypes();
  },
  data() {
    return {
      expanded: false,
      saving: false,
      compiling: false,
      error_message: '',
      editing_timezone: false,
      // The compiled watcher, held between the compile step and the confirm. Non-empty restatement IS
      // the "awaiting confirmation" state — a separate flag could disagree with it.
      compiled_condition: null,
      // The watcher row's own cycle in minutes (compile-watcher), when it runs more often than hourly.
      compiled_interval: null,
      compiled_restatement: '',
      compile_error: '',
      can_watch: [],
      // What Rey can react to, read from the server rather than hardcoded: the event catalog is
      // replaceable at runtime via EventTypeCatalogFile, so a list baked in here would drift.
      event_types: [],
      // OneTime is absent for the reason ScheduledEdit documents: CadenceCalculator returns null for it,
      // and both create and update reject a null NextRunAt, so choosing it always failed.
      cadences: ['Daily', 'Weekdays', 'Weekly', 'Monthly'],
      form: this.blankForm(),
    };
  },
  computed: {
    ...mapStores(useMainStore),
    // One option, still a control: it states what "continuously" means. Hourly is the frequency floor; a row
    // that checks more often (a key out, a voicemail) is created on its own cycle by the server, so the label
    // says that cycle rather than promising "every hour" (NPM-225 review).
    watch_cadence_options() {
      const minutes = this.compiled_interval;
      return [{
        value: 'Hourly',
        label: minutes ? this.$t('scheduled.common.cadenceInterval', { minutes }) : 'Continuously (every hour)',
      }];
    },
    scheduled_availability() {
      return this.mainStore.scheduled_availability;
    },
    push_available() {
      return !!(this.scheduled_availability && this.scheduled_availability.pushNotifications);
    },
    // NPM-237: the environment's watcher switch, from the availability endpoint. Strictly true: an older
    // server that never sends the field, or an answer not loaded yet, keeps the cards hidden — the same
    // closed state PR #1086 hard-coded, now decided per environment.
    watchers_enabled() {
      return !!(this.scheduled_availability && this.scheduled_availability.watchersEnabled === true);
    },
    current_cif() {
      return this.mainStore.current_cif;
    },
    all_dealers() {
      return this.mainStore.all_dealers;
    },
    /**
     * The caller's own rooftops. No "All stores" entry: DealerId is a single CIF that the beta gate, the
     * run's tool scoping and the admin inventory all key on, so there is nothing to store for "all" and
     * nothing for the gate to resolve. The mockup shows the badge; the data model cannot produce it yet.
     */
    store_options() {
      const dealers = Array.isArray(this.all_dealers) ? this.all_dealers : [];
      return dealers
        .filter(d => d && d.cifNumber)
        .map(d => ({ value: d.cifNumber, label: d.name || d.cifNumber }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    timezone_options() {
      return timezoneOptionsFor(this.form.timeZone);
    },
    // The same list with its i18n keys resolved, in the {value, label} shape FusionDropdown reads.
    timezone_dropdown_options() {
      return timezoneDropdownOptions(this.timezone_options, key => this.$t(key));
    },
    weekday_options() {
      return weekdayDropdownOptions();
    },
    time_dropdown_options() {
      return timeOfDayOptions(this.form.time);
    },
    selected_timezone_label() {
      const match = this.timezone_options.find(z => z.value === this.form.timeZone);
      if (!match) return this.form.timeZone;
      return match.labelKey ? this.$t(match.labelKey) : match.label;
    },
    /**
     * What will happen, per type, said before submission. The doc requires one for each: that Rey shows
     * the first result before it repeats, that a watcher stays quiet until the line is crossed, that an
     * event trigger posts when it fires.
     */
    consequence_line() {
      if (this.is_watcher || this.is_event) {
        if (!this.compiled_restatement) return 'Rey reads it back before anything is created.';
        return this.is_event ? 'Creating it starts listening.' : 'Creating it starts the watch.';
      }
      return 'Rey will run this on the schedule above and send you the result.';
    },
    is_recap() {
      return this.form.type === 'Recap';
    },
    is_watcher() {
      return this.form.type === 'Watcher';
    },
    is_event() {
      return this.form.type === 'EventTrigger';
    },
    event_type_labels() {
      return this.event_types.map(t => t.label).filter(Boolean);
    },
    // Says which of the two steps the button is on, so "Create task" never appears over a sentence
    // nobody has read back yet.
    primary_label() {
      if (this.compiling) return 'Reading it back…';
      if (this.saving) return 'Creating…';
      if ((this.is_watcher || this.is_event) && !this.compiled_restatement) return 'Continue';
      return 'Create task';
    },
  },
  watch: {
    // NPM-237: when the cards hide (another store's answer, or one not loaded yet), a form left on Watcher or
    // Event trigger would show fields for a card nobody can see and submit to a create the server refuses.
    watchers_enabled(enabled) {
      if (!enabled && this.form.type !== 'Recap') {
        this.form.type = 'Recap';
        this.onTypeChanged('Recap');
      }
    },
  },
  methods: {
    blankForm() {
      return {
        type: 'Recap',
        name: '',
        prompt: '',
        // The watcher's line, in the person's own words. Kept apart from `prompt` because they are not
        // the same thing: a recap's prompt is run as a turn, a watcher's text is COMPILED once and the
        // condition is what runs. Sharing one field would make the difference invisible here.
        watch_text: '',
        // The event to react to, and the optional instruction from Figure 13. Separate from `prompt`
        // for the same reason watch_text is: one is read once to resolve a trigger, the other is what
        // runs afterwards.
        event_text: '',
        event_action: '',
        cadence: 'Daily',
        time: '07:00',
        dayOfWeek: 1,
        dayOfMonth: 1,
        // The zone the user is actually in. `browserTimeZone()` first for the reason ScheduledEdit
        // records: a fixed American default stamped New York on a task asked for in Paris, and the
        // cadence maths was right while its input was not.
        timeZone: browserTimeZone() || 'America/New_York',
        dealerId: '',
        channels: ['InApp'],
      };
    },
    toggle() {
      if (this.disabled) return;
      this.expanded = !this.expanded;
      // Seeded on open rather than in blankForm(): the active store is not known at construction, and a
      // person who switches rooftops before opening the form should get the one they are looking at.
      if (this.expanded && !this.form.dealerId) this.form.dealerId = this.defaultDealerId();
    },
    defaultDealerId() {
      if (this.current_cif) return this.current_cif;
      const first = this.store_options[0];
      return first ? first.value : '';
    },
    hasChannel(channel) {
      return (this.form.channels || []).includes(channel);
    },
    setChannel(channel, on) {
      const current = new Set(this.form.channels || []);
      if (on) current.add(channel);
      else current.delete(channel);
      // In-app is the floor, exactly as in ScheduledEdit: an empty set is a task that runs and tells
      // nobody, which nothing downstream would report as a problem.
      if (current.size === 0) current.add('InApp');
      this.form.channels = [...current];
    },
    validate() {
      if (this.is_event) {
        if (!this.form.event_text || !this.form.event_text.trim()) {
          this.error_message = 'Please tell Rey what to react to.';
          return false;
        }
        if (!this.form.dealerId) {
          this.error_message = 'Please choose a store.';
          return false;
        }
        this.error_message = '';
        return true;
      }

      if (this.is_watcher) {
        if (!this.form.watch_text || !this.form.watch_text.trim()) {
          this.error_message = 'Please tell Rey what to watch for.';
          return false;
        }
        if (!this.form.dealerId) {
          this.error_message = 'Please choose a store.';
          return false;
        }
        this.error_message = '';
        return true;
      }

      if (!this.form.name || !this.form.name.trim()) {
        this.error_message = 'Please give the task a name.';
        return false;
      }
      if (!this.form.prompt || !this.form.prompt.trim()) {
        this.error_message = 'Please tell Rey what to answer.';
        return false;
      }
      if (!this.form.time) {
        this.error_message = 'Please pick a time.';
        return false;
      }
      if (!this.form.dealerId) {
        this.error_message = 'Please choose a store.';
        return false;
      }
      this.error_message = '';
      return true;
    },
    // Any edit to the watched line invalidates the reading of it. Without this the person could change
    // the sentence and confirm a restatement of the PREVIOUS one — approving something they never typed,
    // which is the exact failure restate-and-confirm exists to prevent.
    clearCompiled() {
      this.compiled_condition = null;
      this.compiled_interval = null;
      this.compiled_restatement = '';
      this.compile_error = '';
      this.can_watch = [];
    },
    // Switching type mid-form must not carry a compiled watcher into a recap, or a recap's cadence into
    // a watcher — a watcher has exactly one cadence and it is not Daily.
    onTypeChanged(type) {
      this.clearCompiled();
      this.error_message = '';
      // An event task has no clock, so its cadence is never read — but it is left at Daily rather than
      // blanked, because the payload builder decides by trigger TYPE and a half-set cadence lying around
      // is one fewer thing that has to agree.
      this.form.cadence = type === 'Watcher' ? 'Hourly' : 'Daily';
    },
    getScheduledEventTypes() {
      const self = this;
      this.mainStore.GetScheduledEventTypes().then((response) => {
        self.event_types = Array.isArray(response) ? response : [];
      }, () => {
        // An empty list stands the Event trigger card down rather than offering a form whose subject
        // could never resolve. Silent: someone opening this to create a recap does not need to hear
        // that an unrelated catalog failed to load.
        self.event_types = [];
      });
    },
    compileEvent() {
      if (!this.validate()) return Promise.resolve(false);

      const self = this;
      this.compiling = true;
      this.compile_error = '';
      this.can_watch = [];
      return this.mainStore.CompileEventTrigger({
        text: this.form.event_text.trim(),
        dealerId: this.form.dealerId,
      }).then((response) => {
        self.compiling = false;
        if (!response || response.ok !== true) {
          self.compile_error = (response && response.error) || "Rey couldn't read that back.";
          self.can_watch = (response && response.canReactTo) || [];
          return false;
        }
        self.compiled_condition = response.trigger;
        self.compiled_restatement = response.restatement || '';
        return true;
      }, () => {
        self.compiling = false;
        self.compile_error = "Couldn't reach Rey to read that back. Please try again.";
        return false;
      });
    },
    buildPayload() {
      const f = this.form;
      const trigger = {
        type: 'Time',
        cadence: f.cadence,
        time: f.time,
        timeZone: f.timeZone || browserTimeZone() || '',
      };
      if (f.cadence === 'Weekly') trigger.dayOfWeek = f.dayOfWeek;
      if (f.cadence === 'Monthly') trigger.dayOfMonth = f.dayOfMonth;
      // An hourly task fires every hour, so only the MINUTE of this value is read by the scheduler.
      // Sent anyway rather than blanked: the server's own default would put every watcher in the
      // fleet on the same minute.

      if (this.is_event) {
        const text = f.event_text.trim();
        return {
          name: text.length > 120 ? `${text.slice(0, 117)}...` : text,
          // The optional instruction becomes the prompt the executor runs when the event fires. Empty
          // is legal and means "just tell me it happened", which is why this is not validated.
          prompt: (f.event_action || '').trim(),
          // Still a ScheduledQuestion: what makes it event-driven is the trigger, not the type. Adding
          // an enum member would have meant a Cosmos migration for something the model already says.
          taskType: 'ScheduledQuestion',
          enabled: true,
          delivery: { channels: f.channels, emailEnabled: f.channels.includes('Email') },
          trigger: {
            // The seam that keeps this off the clock. A "Time" trigger here would satisfy the due query
            // as well as the event query and alert twice for one happening.
            type: 'Event',
            events: [this.compiled_condition],
            timeZone: f.timeZone || browserTimeZone() || '',
          },
          dealerId: f.dealerId,
        };
      }

      if (this.is_watcher) {
        const text = f.watch_text.trim();
        return {
          // Named from the line it watches — the same sentence the row will show, so the list reads
          // back what the person actually typed rather than a label they never chose.
          name: text.length > 120 ? `${text.slice(0, 117)}...` : text,
          prompt: text,
          taskType: 'Watcher',
          enabled: true,
          // The compiled condition, not the prose. The prose was read once, at creation, under a
          // restatement the person confirmed; every run after that evaluates this structure in code and
          // never re-reads the sentence.
          condition: this.compiled_condition,
          delivery: { channels: f.channels, emailEnabled: f.channels.includes('Email') },
          trigger,
          dealerId: f.dealerId,
        };
      }

      return {
        name: f.name.trim(),
        prompt: f.prompt.trim(),
        // Recap is this form's word; ScheduledQuestion is the enum's.
        taskType: 'ScheduledQuestion',
        enabled: true,
        delivery: { channels: f.channels, emailEnabled: f.channels.includes('Email') },
        trigger,
        dealerId: f.dealerId,
      };
      // Deliberately no `storeHoursAnchor`: the API reads an absent anchor as "leave this alone" and an
      // explicit None as "clear it". This form does not offer the control, so it must stay silent rather
      // than send a value the person never chose.
    },
    /**
     * The primary button. A recap creates straight away; a watcher compiles first and only creates once
     * the person has confirmed the reading — compiling is not agreeing.
     */
    submit() {
      if (this.saving || this.compiling) return Promise.resolve(false);
      if (this.is_watcher && !this.compiled_restatement) return this.compileWatcher();
      if (this.is_event && !this.compiled_restatement) return this.compileEvent();
      return this.create();
    },
    compileWatcher() {
      if (!this.validate()) return Promise.resolve(false);

      const self = this;
      this.compiling = true;
      this.compile_error = '';
      this.can_watch = [];
      return this.mainStore.CompileWatcherCondition({
        text: this.form.watch_text.trim(),
        dealerId: this.form.dealerId,
      }).then((response) => {
        self.compiling = false;
        // A refusal arrives as 200 with ok:false — an unresolved subject is a conversational outcome,
        // not a failed request. Rendered beside the box rather than thrown away.
        if (!response || response.ok !== true) {
          self.compile_error = (response && response.error) || "Rey couldn't read that back.";
          self.can_watch = (response && response.canWatch) || [];
          return false;
        }
        self.compiled_condition = response.condition;
        self.compiled_interval = typeof response.intervalMinutes === 'number' ? response.intervalMinutes : null;
        self.compiled_restatement = response.restatement || '';
        return true;
      }, () => {
        self.compiling = false;
        self.compile_error = "Couldn't reach Rey to read that back. Please try again.";
        return false;
      });
    },
    create() {
      if (this.saving) return Promise.resolve(false);
      if (!this.validate()) return Promise.resolve(false);

      const self = this;
      this.saving = true;
      return this.mainStore.SaveScheduledTask(this.buildPayload()).then((saved) => {
        self.saving = false;
        // Collapsed and blanked on success, so the list the person just added to is what they see next.
        self.form = self.blankForm();
        self.editing_timezone = false;
        self.expanded = false;
        self.clearCompiled();
        self.$emit('created', saved);
        return saved;
      }, (error) => {
        self.saving = false;
        // At the cap, "try again" would only be refused again: say what the server said instead.
        if (error && error.code === 'task_cap_reached' && error.message) {
          self.error_message = error.message;
          return false;
        }
        self.error_message = "Couldn't create that task. Please try again.";
        return false;
      });
    },
  },
};
</script>
